import { err, ok } from '@src/services/common/Result';
import { LogManager } from '@src/utility/Logger';

import { extractErrorInfo } from '../utility/error';

import type { CachedVideoRepository } from '@src/repositories/CachedVideoRepository';
import type { StoryRepository } from '@src/repositories/StoryRepository';
import type { VideoRepository } from '@src/repositories/VideoRepository';
import type { Result } from '@src/services/common/Result';
import type { AssociatedVideo, Story } from '@src/types.ts/story';

// Narrow interface to decouple from full MobX store surface.
// Only specify the properties we actually need from currentVideo
export interface VideoCreatorStoreLike {
  story?: Story;
  currentVideo?: {
    id?: string;
    parentVideo?: {
      id?: string;
      associatedVideos?: AssociatedVideo[];
    };
    associatedVideos?: AssociatedVideo[];
  };
  removeStoryVideo: (videoId?: string) => Promise<void>;
  removeStoryVideos: (videoIds: string[]) => Promise<void>;
  loadVideo: (videoId: string) => Promise<void>;
  datoClientStore: {
    videoRepository: VideoRepository | CachedVideoRepository;
    storyRepository: StoryRepository;
  };
}

export interface VideoDeletionServiceDeps {
  videoCreator: VideoCreatorStoreLike;
}

export interface DeleteVideosResult {
  nextVideoId: string;
  switchedToNextVideo: boolean;
}

/**
 * Service responsible for handling video deletion logic.
 * Supports both simple deletion (single video) and complex multi-video deletion
 * with parent/associated video relationships.
 */
export class VideoDeletionService {
  private readonly videoCreator: VideoCreatorStoreLike;
  private readonly logger = LogManager.getLogger(
    'service:VideoDeletionService',
  );

  constructor(deps: VideoDeletionServiceDeps) {
    this.videoCreator = deps.videoCreator;
  }

  /**
   * Delete multiple videos with support for parent/associated video relationships.
   * Handles three cases:
   * 1. Deleting all videos (main + all associated)
   * 2. Deleting main video but keeping some associated (promotes first associated to main)
   * 3. Deleting only associated videos (updates main video's associated list)
   *
   * @param selectedVideoIds - Array of video IDs to delete
   * @param mainVideoId - ID of the main/parent video
   * @param associatedVideos - Array of associated videos
   * @param allVideoOptions - Array of all available videos for next video calculation
   * @returns Result with next video ID and whether a video switch occurred
   */
  public async deleteSelectedVideos(
    selectedVideoIds: string[],
    mainVideoId: string,
    associatedVideos: AssociatedVideo[],
    allVideoOptions: Array<{ id: string }>,
  ): Promise<Result<DeleteVideosResult>> {
    try {
      if (selectedVideoIds.length === 0) {
        return err('VALIDATION', 'No videos selected for deletion');
      }

      if (!mainVideoId) {
        return err('VALIDATION', 'Main video ID is required');
      }

      const isDeletingMainVideo = selectedVideoIds.includes(mainVideoId);
      const deletingAssociatedVideoIds = selectedVideoIds.filter(
        id => id !== mainVideoId,
      );

      const switchedToNextVideo =
        this.shouldSwitchToNextVideo(selectedVideoIds);

      // Case 1: Deleting all videos (main + all associated)
      if (
        isDeletingMainVideo &&
        deletingAssociatedVideoIds.length === associatedVideos.length
      ) {
        return await this.deleteAllVideos(
          mainVideoId,
          allVideoOptions,
          switchedToNextVideo,
        );
      }

      // Case 2: Deleting main video but keeping some associated videos
      if (
        isDeletingMainVideo &&
        deletingAssociatedVideoIds.length < associatedVideos.length
      ) {
        return await this.deleteMainVideoAndPromoteAssociated(
          mainVideoId,
          selectedVideoIds,
          associatedVideos,
          switchedToNextVideo,
        );
      }

      // Case 3: Deleting only associated videos (not the main video)
      if (!isDeletingMainVideo && deletingAssociatedVideoIds.length > 0) {
        return await this.deleteAssociatedVideos(
          mainVideoId,
          deletingAssociatedVideoIds,
          associatedVideos,
          switchedToNextVideo,
        );
      }

      return err('VALIDATION', 'Invalid deletion configuration');
    } catch (e) {
      this.logger.error('Failed to delete selected videos', {
        selectedVideoIds,
        mainVideoId,
        errorInfo: extractErrorInfo(e),
      });
      return err('UNKNOWN', 'Failed to delete selected videos');
    }
  }

  /**
   * Delete several clips outright, in one write (PLAT-11118).
   *
   * This is Case 1 applied to a whole selection: a group delete always takes a
   * clip with *all* of its associated videos, and an associated video is not
   * listed in the story's `other_videos` at all — it hangs off its parent
   * record — so removing the parent ids is the entire operation. None of the
   * promote / relink handling in Cases 2 and 3 can be reached from here.
   *
   * All or nothing by construction: one `other_videos` write either lands or
   * it does not, so there is no half-deleted selection to reconcile.
   */
  public async deleteVideos(mainVideoIds: string[]): Promise<Result<void>> {
    const ids = mainVideoIds.filter(Boolean);
    if (ids.length === 0) {
      return err('VALIDATION', 'No videos selected for deletion');
    }

    try {
      await this.videoCreator.removeStoryVideos(ids);
      return ok(undefined);
    } catch (e) {
      this.logger.error('Failed to delete videos', {
        videoIds: ids,
        errorInfo: extractErrorInfo(e),
      });
      return err('UNKNOWN', 'Failed to delete videos');
    }
  }

  /**
   * Case 1: Delete all videos (main + all associated)
   */
  private async deleteAllVideos(
    mainVideoId: string,
    allVideoOptions: Array<{ id: string }>,
    switchedToNextVideo: boolean,
  ): Promise<Result<DeleteVideosResult>> {
    const nextVideoId = this.getNextVideoIdAfterDeletion(
      mainVideoId,
      allVideoOptions,
    );

    await this.videoCreator.removeStoryVideo(mainVideoId);

    return ok({
      nextVideoId,
      switchedToNextVideo,
    });
  }

  /**
   * Case 2: Delete main video but keep some associated videos.
   * Promotes the first remaining associated video to be the new main.
   */
  private async deleteMainVideoAndPromoteAssociated(
    mainVideoId: string,
    selectedVideoIds: string[],
    associatedVideos: AssociatedVideo[],
    switchedToNextVideo: boolean,
  ): Promise<Result<DeleteVideosResult>> {
    // Find remaining associated videos
    const remainingAssociatedVideos = associatedVideos.filter(
      av => av.id && !selectedVideoIds.includes(av.id),
    );

    // Promote the first remaining associated video to be the new main
    const newMainVideo = remainingAssociatedVideos[0];
    const newAssociatedVideos = remainingAssociatedVideos.slice(1);

    if (!this.videoCreator.story?.id) {
      this.logger.error('Story ID is missing, cannot proceed with deletion.', {
        story: this.videoCreator.story,
      });
      return err('PRECONDITION', 'Story ID is missing');
    }

    if (!newMainVideo.id) {
      this.logger.error(
        'New main video has no ID, cannot proceed with deletion.',
        { newMainVideo },
      );
      return err('PRECONDITION', 'New main video has no ID');
    }

    // Step 1: Clear the old main video's associated_videos to break parent-child links
    // This removes the parentVideo back-reference from all associated videos
    await this.videoCreator.datoClientStore.videoRepository.updateVideo({
      id: mainVideoId,
      associatedVideos: [],
    });

    // Step 2: Remove old main video from story
    await this.videoCreator.removeStoryVideo(mainVideoId);

    // Step 3: Update the promoted video (now parentless) to have the remaining associated videos
    await this.videoCreator.datoClientStore.videoRepository.updateVideo({
      id: newMainVideo.id,
      associatedVideos: newAssociatedVideos,
    });

    // Step 4: Add new main video to story
    await this.videoCreator.datoClientStore.storyRepository.addVideoToStory(
      this.videoCreator.story.id,
      newMainVideo.id,
    );

    // Step 5: Fetch full video data for the promoted video
    // AssociatedVideo lacks fields that main videos have (title, slug, hash, etc.)
    const { videoVersions } =
      await this.videoCreator.datoClientStore.videoRepository.getVersionsByVideoId(
        newMainVideo.id,
        1,
      );

    if (!videoVersions?.[0]) {
      this.logger.error('Failed to fetch promoted video data after promotion', {
        newMainVideoId: newMainVideo.id,
      });
      return err('UNKNOWN', 'Failed to fetch promoted video data');
    }

    const fullVideoData = videoVersions[0];

    // Step 6: Update story.otherVideos with the promoted video (full data)
    if (this.videoCreator.story.otherVideos) {
      this.videoCreator.story.otherVideos = [
        fullVideoData,
        ...this.videoCreator.story.otherVideos.filter(
          v => v.id !== mainVideoId,
        ),
      ];
    }

    return ok({
      nextVideoId: newMainVideo.id,
      switchedToNextVideo,
    });
  }

  /**
   * Case 3: Delete only associated videos (not the main video).
   * Updates the main video's associated videos list.
   */
  private async deleteAssociatedVideos(
    mainVideoId: string,
    deletingAssociatedVideoIds: string[],
    associatedVideos: AssociatedVideo[],
    switchedToNextVideo: boolean,
  ): Promise<Result<DeleteVideosResult>> {
    const remainingAssociatedVideos = associatedVideos.filter(
      av => av.id && !deletingAssociatedVideoIds.includes(av.id),
    );

    // Update the main video's associated videos list
    await this.videoCreator.datoClientStore.videoRepository.updateVideo({
      id: mainVideoId,
      associatedVideos: remainingAssociatedVideos,
    });

    // Update local state
    if (this.videoCreator.story?.otherVideos) {
      const mainStoryVideo = this.videoCreator.story.otherVideos.find(
        v => v.id === mainVideoId,
      );
      if (mainStoryVideo) {
        mainStoryVideo.associatedVideos = remainingAssociatedVideos;
      }
    }

    // Update currentVideo's associatedVideos if it's the main video or its parent
    if (this.videoCreator.currentVideo) {
      const currentMainVideo =
        this.videoCreator.currentVideo.parentVideo ||
        this.videoCreator.currentVideo;
      if (currentMainVideo.id === mainVideoId) {
        currentMainVideo.associatedVideos = remainingAssociatedVideos;
      }
    }

    return ok({
      nextVideoId: mainVideoId,
      switchedToNextVideo,
    });
  }

  /**
   * Determine if we should switch to a different video after deletion.
   * Returns true if the currently loaded video is in the list of videos being deleted.
   *
   * @param deletingVideoIds - Array of video IDs being deleted
   * @returns true if current video is being deleted, false otherwise
   */
  private shouldSwitchToNextVideo(deletingVideoIds: string[]): boolean {
    const currentVideoId = this.videoCreator.currentVideo?.id;
    return !!(currentVideoId && deletingVideoIds.includes(currentVideoId));
  }

  /**
   * Calculate the next video ID to select after a video is deleted.
   * Uses the provided allVideoOptions to determine the next available video.
   *
   * @param deletedVideoId - ID of the video being deleted
   * @param allVideoOptions - Array of all available videos (from useMemo in hook)
   * @returns ID of the next video to select, or 'original_story' if none available
   */
  private getNextVideoIdAfterDeletion(
    deletedVideoId: string,
    allVideoOptions: Array<{ id: string }>,
  ): string {
    const idx = allVideoOptions.findIndex(data => data.id === deletedVideoId);

    if (idx === -1) {
      // Video not found, default to original_story
      return 'original_story';
    }

    // Try next video
    let nextVideoIdx = idx + 1;

    // If we've gone past the end, try previous video
    if (nextVideoIdx >= allVideoOptions.length) {
      nextVideoIdx = idx - 1;
    }

    // If still no valid video (was the only one), return original_story
    if (nextVideoIdx < 0 || nextVideoIdx >= allVideoOptions.length) {
      return 'original_story';
    }

    return allVideoOptions[nextVideoIdx].id;
  }
}

export default VideoDeletionService;
